import React, { useState, useEffect, useMemo } from 'react';
import {
  getBOMs, getBOMTree, getBOMWhereUsed, mrpExplode,
  getSkus, promoteToDraftPo,
} from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  TreeStructure, MagnifyingGlass, Lightning, Warning, Package,
  CheckCircle, ShoppingCart, ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';


// --------------------------------------------------------------------------- //
// Flatten a nested BOM tree into display rows (iterative — avoids JSX recursion)
// --------------------------------------------------------------------------- //

function flattenTree(node, depth, rows) {
  rows.push({ kind: 'bom', depth, node });
  const components = node.components || [];
  components.forEach((c, i) => {
    if (c.type === 'bom') {
      rows.push({ kind: 'sub-line', depth: depth + 1, comp: c, key: `sub-${depth}-${i}` });
      if (!c.cycle_detected && c.subtree) {
        flattenTree(c.subtree, depth + 2, rows);
      }
    } else {
      rows.push({ kind: 'raw', depth: depth + 1, comp: c, key: `raw-${depth}-${i}` });
    }
  });
}

function BomRow({ row }) {
  const pad = { paddingLeft: `${row.depth * 20}px` };
  if (row.kind === 'bom') {
    const n = row.node;
    return (
      <div
        className="flex items-center gap-2 py-1.5 border-b border-border hover:bg-muted"
        style={pad}
        data-testid={`bom-tree-node-${n.bom_id || 'root'}`}
      >
        <Package size={16} className="text-brand" weight="duotone" />
        <div className="flex-1 text-sm">
          <span className="font-mono font-bold">{n.bom_number || ''}</span>
          <span className="opacity-70 ml-2">{n.bom_name}</span>
          {n.version ? (<span className="text-[10px] opacity-50 font-mono ml-2">v{n.version}</span>) : null}
        </div>
        <div className="font-mono text-xs opacity-70">
          x{(n.multiplier || 1).toFixed(2)} units
        </div>
        <div className="font-mono text-sm font-bold tabular-nums w-28 text-right">
          ${(n.rolled_up_cost || 0).toFixed(2)}
        </div>
      </div>
    );
  }
  if (row.kind === 'sub-line') {
    const c = row.comp;
    return (
      <div
        className="flex items-center gap-2 py-1 text-xs border-b border-border bg-blue-50/30"
        style={pad}
      >
        <TreeStructure size={14} className="text-blue-600" />
        <span className="flex-1 opacity-70">
          sub-assembly · qty {c.quantity_per_unit}
          {c.scrap_pct > 0 ? (<span className="text-amber-600 ml-1">+{c.scrap_pct}% scrap</span>) : null}
          {c.cycle_detected ? (<span className="text-red-600 font-bold ml-2">CYCLE DETECTED</span>) : null}
        </span>
        <span className="font-mono opacity-60">effective {c.effective_qty}</span>
        <span className="font-mono font-bold w-28 text-right">${(c.line_cost || 0).toFixed(2)}</span>
      </div>
    );
  }
  // raw
  const c = row.comp;
  const short = (c.available_qty || 0) < (c.effective_qty || 0);
  return (
    <div
      className={`flex items-center gap-2 py-1 text-xs border-b border-border ${short ? 'bg-red-50/40' : ''}`}
      style={pad}
      data-testid={`bom-tree-raw-${c.sku_code}`}
    >
      <span className="inline-block w-2 h-2 rounded-full bg-slate-400" />
      <span className="font-mono font-bold">{c.sku_code || ''}</span>
      <span className="opacity-60 truncate max-w-xs">{c.sku_name}</span>
      <span className="font-mono opacity-60">
        qty {c.quantity_per_unit}
        {c.scrap_pct > 0 ? (<span className="text-amber-600 ml-1">+{c.scrap_pct}%</span>) : null}
      </span>
      <span className="ml-auto font-mono text-xs">
        <span className={short ? 'text-red-600 font-bold' : 'opacity-70'}>
          need {c.effective_qty} · on-hand {c.available_qty || 0}
        </span>
      </span>
      <span className="font-mono font-bold w-28 text-right">${(c.line_cost || 0).toFixed(2)}</span>
    </div>
  );
}

function TreeNode({ node }) {
  const rows = [];
  flattenTree(node, 0, rows);
  return (
    <div>
      {rows.map((row, i) => (<BomRow key={row.key || `root-${i}`} row={row} />))}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Tab 1 — BOM Tree drilldown
// --------------------------------------------------------------------------- //

export function BOMTreeTab({ boms }) {
  const [bomId, setBomId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!bomId) return;
    setLoading(true);
    try {
      const r = await getBOMTree(bomId, quantity);
      setTree(r.data.tree);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tree');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (bomId) load(); /* eslint-disable-next-line */ }, [bomId, quantity]);

  return (
    <div className="p-4 space-y-4" data-testid="bom-tree-tab">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[10px] uppercase tracking-wider opacity-60">Select BOM</Label>
          <Select value={bomId} onValueChange={setBomId}>
            <SelectTrigger data-testid="tree-bom-select"><SelectValue placeholder="Pick a BOM to expand" /></SelectTrigger>
            <SelectContent>
              {boms.map(b => (
                <SelectItem key={b.id} value={b.id}>
                  {b.bom_number} · {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider opacity-60">Units to build</Label>
          <Input
            type="number" min={1} value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            className="w-32" data-testid="tree-quantity-input"
          />
        </div>
      </div>

      {loading && <div className="text-sm opacity-60">Expanding tree…</div>}
      {!loading && tree && (
        <Card className="p-3 rounded-none border border-border" data-testid="bom-tree-root">
          <div className="flex items-center gap-2 mb-2 text-xs opacity-60 font-mono uppercase tracking-wider">
            <TreeStructure size={14} /> Nested BOM tree · rolled-up cost across all levels
          </div>
          <TreeNode node={tree} />
        </Card>
      )}
      {!loading && !tree && bomId && (
        <div className="text-sm opacity-60">No tree loaded.</div>
      )}
      {!bomId && (
        <div className="text-sm opacity-60 text-center py-8">
          Pick a BOM above to see its expanded tree — every sub-assembly is drilled into and scrap factors rolled up.
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Tab 2 — Where Used
// --------------------------------------------------------------------------- //

export function WhereUsedTab() {
  const [skus, setSkus] = useState([]);
  const [skuId, setSkuId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getSkus().then(r => setSkus(r.data || [])).catch(() => {}); }, []);

  const load = async () => {
    if (!skuId) return;
    setLoading(true);
    try {
      const r = await getBOMWhereUsed({ sku_id: skuId });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (skuId) load(); /* eslint-disable-next-line */ }, [skuId]);

  const allRows = useMemo(() => {
    if (!result) return [];
    return [
      ...(result.direct || []).map(r => ({ ...r, kind: 'direct' })),
      ...(result.indirect || []).map(r => ({ ...r, kind: 'indirect' })),
    ];
  }, [result]);

  return (
    <div className="p-4 space-y-4" data-testid="where-used-tab">
      <div>
        <Label className="text-[10px] uppercase tracking-wider opacity-60">Component SKU</Label>
        <Select value={skuId} onValueChange={setSkuId}>
          <SelectTrigger data-testid="where-used-sku-select"><SelectValue placeholder="Pick a SKU to reverse-lookup" /></SelectTrigger>
          <SelectContent>
            {skus.map(s => (
              <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="text-sm opacity-60">Looking up…</div>}
      {!loading && result && (
        <Card className="rounded-none border border-border" data-testid="where-used-results">
          <div className="p-3 border-b border-border flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
            <MagnifyingGlass size={14} />
            {result.count === 0 ? 'Not used in any BOM' : (
              <>
                Found in {result.count} BOM{result.count !== 1 ? 's' : ''} ·
                <span className="text-green-700 ml-1">{result.direct?.length || 0} direct</span> ·
                <span className="text-blue-700 ml-1">{result.indirect?.length || 0} indirect</span>
              </>
            )}
          </div>
          {allRows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-muted text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-left px-3 py-2">BOM</th>
                  <th className="text-left px-3 py-2">Finished Good</th>
                  <th className="text-left px-3 py-2">Version</th>
                  <th className="text-right px-3 py-2">Context</th>
                </tr>
              </thead>
              <tbody>
                {allRows.map((r, i) => (
                  <tr key={r.bom_id + r.kind} className="border-t border-slate-100" data-testid={`where-used-row-${i}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${r.kind === 'direct' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {r.kind}{r.hops_to_target ? ` · +${r.hops_to_target}` : ''}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-mono font-bold">{r.bom_number}</div>
                      <div className="text-xs opacity-70">{r.bom_name}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="font-mono font-bold">{r.finished_good_sku_code}</span>
                      <span className="opacity-70 ml-1">{r.finished_good_name}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs opacity-70">{r.version}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono opacity-70">
                      {r.lines
                        ? r.lines.map(l => `${l.quantity}u${l.scrap_pct ? ` +${l.scrap_pct}%` : ''}`).join(', ')
                        : 'via sub-assembly'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
      {!skuId && (
        <div className="text-sm opacity-60 text-center py-8">
          Pick a component SKU above to see every finished-good BOM that consumes it — directly or via sub-assembly.
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Tab 3 — MRP Explosion
// --------------------------------------------------------------------------- //

export function MRPExplosionTab({ boms }) {
  const [bomId, setBomId] = useState('');
  const [quantity, setQuantity] = useState(100);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoted, setPromoted] = useState(new Set());

  const run = async () => {
    if (!bomId) return;
    setLoading(true);
    try {
      const r = await mrpExplode({ bom_id: bomId, quantity });
      setResult(r.data);
      setPromoted(new Set());
      const short = r.data.raw_material_shopping_list.filter(l => l.action === 'order').length;
      toast.success(`MRP complete · ${r.data.distinct_raw_components} raw components · ${short} short`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'MRP explode failed');
    } finally {
      setLoading(false);
    }
  };

  const promote = async (line) => {
    if (!line.sku_code || !line.variant_id) {
      toast.error('Missing SKU data, cannot draft PO');
      return;
    }
    // Need sku_id: look up from skus list via sku_code
    try {
      const sku = (await getSkus()).data.find(s => s.sku_code === line.sku_code);
      if (!sku) throw new Error('SKU not found');
      await promoteToDraftPo({
        sku_id: sku.id,
        sku_code: line.sku_code,
        sku_name: line.sku_name,
        recommended_qty: Math.ceil(line.net_required),
        scenario_name: `MRP for BOM ${boms.find(b => b.id === bomId)?.bom_number || ''} × ${quantity}`,
        notes: `MRP shortage: need ${line.gross_required}, on-hand ${line.on_hand}`,
      });
      setPromoted(new Set([...promoted, line.variant_id]));
      toast.success(`Draft PO created for ${line.sku_code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Promote failed');
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="mrp-explosion-tab">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[10px] uppercase tracking-wider opacity-60">Target BOM</Label>
          <Select value={bomId} onValueChange={setBomId}>
            <SelectTrigger data-testid="mrp-bom-select"><SelectValue placeholder="Pick a BOM to explode" /></SelectTrigger>
            <SelectContent>
              {boms.map(b => (
                <SelectItem key={b.id} value={b.id}>
                  {b.bom_number} · {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider opacity-60">Build quantity</Label>
          <Input
            type="number" min={1} value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            className="w-32" data-testid="mrp-quantity-input"
          />
        </div>
        <Button onClick={run} disabled={!bomId || loading} className="bg-brand hover:bg-brand/90" data-testid="mrp-run-btn">
          <Lightning size={16} className="mr-1" weight="fill" />
          {loading ? 'Exploding…' : 'Run MRP'}
        </Button>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-3 rounded-none border border-border">
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Raw components</div>
              <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="mrp-kpi-components">
                {result.distinct_raw_components}
              </div>
            </Card>
            <Card className="p-3 rounded-none border border-border">
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Target build</div>
              <div className="text-2xl font-bold mt-1 tabular-nums">
                {result.target_quantity}
              </div>
            </Card>
            <Card className="p-3 rounded-none border border-border">
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Rolled-up BOM cost</div>
              <div className="text-2xl font-bold mt-1 tabular-nums">
                ${result.tree_rolled_up_cost?.toFixed(2)}
              </div>
            </Card>
            <Card className={`p-3 rounded-none border-2 ${result.total_shortage_cost > 0 ? 'border-red-400 bg-red-50/40' : 'border-emerald-400 bg-emerald-50/40'}`}>
              <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Shortage cost</div>
              <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="mrp-kpi-shortage">
                ${result.total_shortage_cost?.toFixed(2)}
              </div>
            </Card>
          </div>

          {result.has_cycle && (
            <div className="p-3 border border-red-300 bg-red-50 text-sm text-red-700 flex items-center gap-2" data-testid="mrp-cycle-warning">
              <Warning size={16} weight="fill" />
              Cycle detected in BOM tree — a sub-assembly references itself (directly or indirectly). Fix the BOM before using MRP output.
            </div>
          )}

          <Card className="overflow-hidden rounded-none border border-border">
            <div className="p-3 border-b border-border flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
              <ShoppingCart size={14} />
              Raw-material shopping list · worst shortages first
            </div>
            <table className="w-full text-sm" data-testid="mrp-shopping-table">
              <thead className="bg-muted text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-right px-3 py-2">Gross need</th>
                  <th className="text-right px-3 py-2">On-hand</th>
                  <th className="text-right px-3 py-2">Net need</th>
                  <th className="text-right px-3 py-2">Unit $</th>
                  <th className="text-right px-3 py-2">Shortage $</th>
                  <th className="text-left px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {result.raw_material_shopping_list.map((line, i) => {
                  const short = line.action === 'order';
                  return (
                    <tr key={line.variant_id} className={`border-t border-slate-100 ${short ? 'bg-red-50/40' : ''}`} data-testid={`mrp-line-${line.sku_code || i}`}>
                      <td className="px-3 py-2">
                        <div className="font-mono font-bold">{line.sku_code}</div>
                        <div className="text-xs opacity-70">{line.sku_name}</div>
                        {line.via_phantom && (
                          <div className="text-[10px] text-purple-600 font-mono mt-0.5">via phantom {line.via_phantom}</div>
                        )}
                        {line.alternates_used && line.alternates_used.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {line.alternates_used.map((alt, ai) => (
                              <span key={ai} className="inline-block text-[10px] font-mono bg-blue-100 text-blue-700 px-1.5 py-0.5" title={`priority ${alt.priority}`}>
                                alt: {alt.sku_code} (−{alt.qty_consumed})
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{line.gross_required}</td>
                      <td className="px-3 py-2 text-right font-mono opacity-70">{line.on_hand}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${short ? 'text-red-700' : 'text-emerald-700'}`}>
                        {line.net_required}
                      </td>
                      <td className="px-3 py-2 text-right font-mono opacity-70">${line.unit_cost}</td>
                      <td className={`px-3 py-2 text-right font-mono ${short ? 'text-red-700 font-bold' : 'opacity-40'}`}>
                        ${line.shortage_cost}
                      </td>
                      <td className="px-3 py-2">
                        {short ? (
                          promoted.has(line.variant_id) ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-mono font-bold" data-testid={`mrp-promoted-${line.sku_code}`}>
                              <CheckCircle size={14} weight="fill" /> Drafted
                            </span>
                          ) : (
                            <Button size="sm" variant="default" onClick={() => promote(line)} className="h-7 text-xs bg-brand hover:bg-brand/90" data-testid={`mrp-promote-${line.sku_code}`}>
                              <ArrowRight size={12} className="mr-1" /> Draft PO
                            </Button>
                          )
                        ) : line.action === 'covered_by_alternates' ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-mono font-bold">
                            <CheckCircle size={12} weight="fill" /> via alternates
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                            <CheckCircle size={12} /> sufficient
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}
      {!result && !loading && (
        <div className="text-sm opacity-60 text-center py-8">
          Pick a BOM, set a target build qty, and hit Run MRP — we'll walk every sub-assembly, net against on-hand, and surface the exact raw-material POs you need to raise.
        </div>
      )}
    </div>
  );
}
